/**
 * The terms of service.
 *
 * Short on purpose. The obligations that actually matter here are narrow: it is your GitHub
 * account, the assistant acts immediately rather than asking for approval, and you are
 * responsible for what it does with your permission. Saying that plainly is more use to
 * somebody than four pages of boilerplate they will not read.
 */
import { env } from '@/config/env'
import { Bullets, LegalDocument, Section } from './LegalDocument'

export function TermsPage() {
  return (
    <LegalDocument
      title="Terms of Service"
      summary="The agreement between you and the operator of this service."
    >
      <Section heading="Agreement">
        <p>
          By creating an account or signing in, you agree to these terms. They are between you
          and {env.legal.operator}, who operates this deployment. If you do not agree, do not
          use the service.
        </p>
      </Section>

      <Section heading="What the service does">
        <p>
          RepoPilot connects to your GitHub account and lets an AI assistant read and change
          repositories on your behalf — listing repositories, reading issues, and creating,
          updating, labelling and closing them.
        </p>
        <p>
          <strong className="font-semibold text-ink">
            Changes take effect immediately, without a separate confirmation step.
          </strong>{' '}
          When you ask the assistant to create or close an issue, it does so. Every action is
          recorded on the Executions page so you can see exactly what ran.
        </p>
      </Section>

      <Section heading="Your account">
        <Bullets
          items={[
            'You are responsible for what happens under your account, including anything the assistant does with the GitHub access you grant it.',
            'Keep your password and your sessions to yourself, and end any session you do not recognise.',
            'Only connect a GitHub account you are entitled to use, and only grant access to repositories you are permitted to act on.',
            'You must be at least 16 years old.',
          ]}
        />
      </Section>

      <Section heading="Acceptable use">
        <p>Do not use the service to:</p>
        <Bullets
          items={[
            'Break the law, or infringe anyone else’s rights.',
            'Reach repositories or accounts you are not authorised to reach, or attempt to act as another user of this service.',
            'Interfere with the service, work around its limits, or use it to attack GitHub or anyone else.',
            'Generate spam, or automate abuse of GitHub’s API.',
          ]}
        />
        <p>
          GitHub’s own terms still apply to everything the assistant does in your name, and
          GitHub decides what your token is allowed to reach. This service never grants access
          GitHub has not already given you.
        </p>
      </Section>

      <Section heading="Availability">
        <p>
          The service is provided as it is, with no promise of uptime. It depends on GitHub, on
          an AI model provider and on hosting we do not control, and any of them can be slow or
          unavailable. Features may change or be withdrawn.
        </p>
      </Section>

      <Section heading="No warranty and limits of liability">
        <p>
          The service is provided “as is”, without warranties of any kind, to the extent the
          law allows. An AI assistant can be wrong: check what it did before relying on it. To
          the extent the law allows, the operator is not liable for indirect or consequential
          loss, lost data, or lost profits arising from your use of the service.
        </p>
      </Section>

      <Section heading="Ending it">
        <p>
          You can stop at any time: disconnect GitHub, and ask for your account to be deleted.
          The operator may suspend or end an account that breaches these terms or puts the
          service or other users at risk.
        </p>
      </Section>

      <Section heading="Privacy">
        <p>
          What the service stores and why is set out in the Privacy Policy, which forms part of
          these terms.
        </p>
      </Section>

      <Section heading="Changes">
        <p>
          These terms can change. The date at the top shows when they last did; continuing to
          use the service after a change means you accept it.
        </p>
      </Section>
    </LegalDocument>
  )
}
